import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { getInformation, login } from '../../api';
import { AuthForm, AuthFormButton, AuthFormInput, AuthFormLabel, AuthLoading } from '../../features/auth';
import logo from '../../images/logologin.png';
import { translateAuthError } from '../../lib/authError';
import LanguageControl from '../../shared/components/LanguageControl';

const Login = (props: any) => {
    const { setCompanyName, setLogin } = props;
    const { t } = useTranslation(undefined, { keyPrefix: 'Login' });

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const entryPageRoutes = {
        overview: '/overview',
        uploaded: '/uploaded',
        pending: '/pending',
        realtime: '/realtime',
        setting: '/setting',
    };

    const withTimeout = (promise: any, ms: any) =>
        Promise.race([
            promise,
            new Promise((_: any, reject: any) => setTimeout(() => reject(new Error('timeout')), ms)),
        ]);

    const onSubmit = async (event: any) => {
        setLoading(true);
        event.preventDefault();
        const email = event.target.email.value;
        const password = event.target.password.value;

        try {
            const data = await login(email, password);
            if (!(data && data.token)) {
                // Legacy path: backend still returns 200 with { error } in the
                // body. Once the status-code fix ships, this branch stops
                // firing on auth failure — the catch below takes over — but
                // it's kept as a safety net until the deploy is confirmed.
                setLoading(false);
                alert(translateAuthError(t, data, 'authError.loginFailed'));
                return;
            }

            localStorage.setItem('token', data.token);
            setCompanyName(data.company_name);
            setLogin(true);

            let entryPage = null;
            try {
                const info = await withTimeout(getInformation(), 2000);
                entryPage = info?.user?.preference?.entry_page || null;
            } catch (error) {
                entryPage = null;
            }

            const targetRoute = entryPageRoutes[entryPage as keyof typeof entryPageRoutes] || '/overview';
            setLoading(false);
            navigate(targetRoute);
        } catch (error) {
            // Post-backend-fix path: axios throws on 4xx, so the specific
            // reason ('Invalid email or password' / 'User not verified' /
            // 'Email and password are required') lives on response.data.error.
            // Falls back to the generic message for network errors, which
            // have no response.
            setLoading(false);
            alert(translateAuthError(t, error, 'authError.loginFailed'));
        }
    };

    if (loading) {
        return <AuthLoading label={t('login')} />;
    }

    return (
        <div className='container-auth'>
            <AuthForm
                className='gap-y-6'
                logoSrc={logo}
                onLogoClick={() => navigate('/')}
                topRight={<LanguageControl className='hidden md:block' />}
            >
                <form onSubmit={onSubmit} className='flex w-[360px] flex-col items-center md:mt-20 md:w-full'>
                    <AuthFormLabel htmlFor='email'>{t('email')}</AuthFormLabel>
                    <AuthFormInput
                        id='email'
                        name='email'
                        type='text'
                        placeholder={t('email')}
                        autoComplete='username'
                    />
                    <AuthFormLabel htmlFor='password'>{t('password')}</AuthFormLabel>
                    <AuthFormInput
                        id='password'
                        name='password'
                        type='password'
                        placeholder={t('password')}
                        autoComplete='current-password'
                    />
                    <div className='mb-16 flex w-[21rem] items-center justify-between'>
                        <div className='flex items-center'>
                            <input type='checkbox' className='mr-2 accent-[#1E2B7C] cursor-pointer' />
                            <label className='text-sm'>{t('rememberMe')}</label>
                        </div>
                        <Link to='/forgot' className='text-sm text-blue-600 hover:underline'>
                            {t('forgotPassword')}
                        </Link>
                    </div>
                    <AuthFormButton className='md:w-[9.25rem] w-[360px] cursor-pointer'>{t('login')}</AuthFormButton>
                </form>
                <div className='md:absolute md:bottom-2 md:left-1/2 md:w-full md:-translate-x-1/2 text-center'>
                    <p className='text-sm'>
                        {t('noAccount')}{' '}
                        <Link to='/signup' className='text-blue-600 hover:underline'>
                            {t('signupForFree')}
                        </Link>
                    </p>
                </div>
                <div className='block md:hidden'>
                    <LanguageControl />
                </div>
            </AuthForm>
        </div>
    );
};

export default Login;
